
import { defineComponent, defineAsyncComponent } from 'vue'
import { mapGetters, mapState } from 'vuex'
import { setI18nLanguage } from '@/i18n'
import { LanguagesType } from '@/types'
import { constants } from '@/constants'
import { RolePermissionDetails } from '@/store/modules/users/interfaces/IUser'
import { getValueByLabel } from '@/utils/permissions'
import { IPortalHardCodeLink } from '@/store/modules/links/interfaces/IPortalHardCodeLink'
import { LinksActionType } from '@/store/modules/links/interfaces'
import DegradedServiceBanner from '@/components/common/DegradedServiceBanner.vue'

const AsahiBsHeader = defineAsyncComponent(() =>
  import('@/components/library/AsahiBsHeader.vue')
)

const AsahiBsFooter = defineAsyncComponent(() =>
  import('@/components/library/AsahiBsFooter.vue')
)

const AsahiBsNav = defineAsyncComponent(() =>
  import('@/components/library/AsahiBsNav.vue')
)

const BreadcrumbLayout = defineAsyncComponent(() =>
  import('@/components/library/BreadcrumbLayout.vue')
)

export default defineComponent({
  name: 'DefaultLayout',
  components: {
    AsahiBsHeader,
    AsahiBsFooter,
    AsahiBsNav,
    BreadcrumbLayout,
    DegradedServiceBanner
  },
  data: () => ({
    activeLanguage: (localStorage.getItem('lang')) as LanguagesType,
    theme: constants.application.theme,
    dictionary: {
      en: {
        createAnOrder: 'CREATE AN ORDER',
        createAnOrderIt: 'Create An Order',
        cart: 'MY CART',
        otherLinks: 'Other links',
        legalClause: 'Legal clause',
        backUp: 'Back up',
        menu: 'Menu',
        tutorial: 'Tutorial',
        search: 'Search',
        home: 'Homepage',
        profile: 'My Profile',
        contracts: 'Contracts',
        orders: 'Orders',
        history: 'History',
        savedOrders: 'Saved Orders',
        suggestedCart: 'Suggested Cart',
        catalog: 'Catalogue',
        products: 'Product Overview',
        favorites: 'My Favorites',
        favoritesMenu: 'MY FAVORITES',
        reporting: 'Reporting',
        myDocuments: 'My documents',
        marketingMaterials: 'Marketing Materials',
        news: 'News and Articles',
        newsEvents: 'News and Events',
        aboutPeroni: 'About Peroni',
        support: 'Support',
        faq: 'FAQ',
        knowledgeBase: 'Knowledge Base',
        supportTickets: 'Support Tickets',
        eLearning: 'Academy',
        skELearning: 'Academy',
        pubMenu: 'Menu Creator',
        skPubMenu: 'Menu Creator',
        emptiesManagement: 'Empties Management',
        contacts: 'Contacts',
        about: 'About us',
        privacyPolicy: 'Privacy Policy',
        cookies: 'Cookies and personal data protection',
        brands: 'Brands',
        productCode: 'Product code',
        createNewUser: 'Create New User',
        trainingLearning: 'Training & Learning',
        Unit: 'Unit',
        languages: {
          cs: 'Česky',
          en: 'English',
          de: 'Deutsch',
          sk: 'Slovensky'
        },
        login: 'Login',
        notificationsEmpty: 'No notifications',
        termsOfUse: 'Terms of use',
        corporateInformation: 'Corporate Information',
        corporateGovernance: 'Corporate Governance',
        goToCart: 'Go To Cart',
        removeItem: 'Remove Item'
      },
      cs: {
        createAnOrder: 'Vytvořit objednávku',
        administration: 'Administrace',
        cart: 'Košík',
        home: 'Hlavní stránka',
        profile: 'Můj profil',
        contracts: 'Přehled smluv',
        orders: 'Objednávky',
        history: 'Historie objednávek',
        savedOrders: 'Uložené objednávky',
        suggestedCart: 'Návrh objednávky',
        catalog: 'Katalog',
        products: 'Přehled výrobků',
        favorites: 'Moje oblíbené',
        favoritesMenu: 'Moje oblíbené',
        reporting: 'Přehled odběrů',
        myDocuments: 'Moje dokumenty',
        marketingMaterials: 'Materiály pro Vás',
        news: 'Novinky a články',
        aboutPeroni: 'O Peroni',
        support: 'Podpora',
        faq: 'Otázky a odpovědi',
        knowledgeBase: 'O portálu',
        supportTickets: 'Kontaktujte nás',
        eLearning: 'Akademie První u zdroje',
        pubMenu: 'Prazdroj menu',
        otherLinks: 'Další odkazy',
        legalClause: 'Právní doložka',
        backUp: 'Zpět nahoru',
        menu: 'Menu',
        tutorial: 'Průvodce',
        search: 'Vyhledat',
        contacts: 'Kontakty',
        about: 'O nás',
        privacyPolicy: 'Ochrana soukromí',
        cookies: 'Cookies a ochrana osobních údajů',
        brands: 'Značky',
        productCode: 'kód produktu',
        createNewUser: 'Vytvořit nového uživatele',
        Unit: 'Balení',
        languages: {
          cs: 'Česky',
          en: 'English',
          de: 'Deutsch',
          sk: 'Slovensky'
        },
        login: 'Přihlásit se',
        notificationsEmpty: 'Žádné notifikace',
        termsOfUse: 'Podmínky použití',
        goToCart: 'Košík',
        removeItem: 'Odstranit'
      },
      sk: {
        createAnOrder: 'Vytvoriť objednávku',
        administration: 'Administrace',
        cart: 'Košík',
        home: 'Hlavná stránka',
        profile: 'Môj profil',
        contracts: 'Prehľad zmlúv',
        orders: 'Objednávky',
        history: 'História objednávok',
        savedOrders: 'Uložené objednávky',
        suggestedCart: 'Odporúčaný tovar',
        catalog: 'Katalóg',
        products: 'Prehľad výrobkov',
        favorites: 'Moje obľúbené',
        favoritesMenu: 'Moje obľúbené',
        reporting: 'Prehľad odberov',
        myDocuments: 'Moje dokumenty',
        marketingMaterials: 'Materiály pre Vás',
        news: 'Novinky a články',
        aboutPeroni: 'O Peroni',
        support: 'Podpora',
        faq: 'Otázky a odpovede',
        knowledgeBase: 'O portáli',
        supportTickets: 'Kontaktujte nás',
        eLearning: 'Akadémia Prvý pri zdroji',
        skELearning: 'Akadémia Prvý pri zdroji',
        pubMenu: 'Prazdroj menu',
        skPubMenu: 'Prazdroj menu',
        otherLinks: 'Ďalšie odkazy',
        legalClause: 'Právna doložka',
        backUp: 'Späť hore',
        menu: 'Menu',
        tutorial: 'Sprievodca',
        search: 'Hľadať',
        contacts: 'Kontakty',
        about: 'O nás',
        privacyPolicy: 'Ochrana súkromia',
        cookies: 'Cookies a ochrana osobných údajov',
        brands: 'Značky',
        productCode: 'kód produktu',
        createNewUser: 'Vytvoriť nového užívateľa',
        Unit: 'Balenie',
        languages: {
          cs: 'Česky',
          en: 'English',
          sk: 'Slovensky'
        },
        login: 'Prihlásiť sa',
        notificationsEmpty: 'Žiadne notifikácie',
        termsOfUse: 'Podmienky používania',
        goToCart: 'Košík',
        removeItem: 'Odstrániť'
      }
    }
  }),

  computed: {
    ...mapState('basket', ['id', 'items', 'progressBarMsg']),
    ...mapState('catalog', ['wishlist', 'favorites']),
    ...mapGetters('main', ['getPermissions']),
    ...mapGetters('basket', ['progressBarMessage', 'progressBarSuccess', 'showProgressBarMessage']),
    isCartPage () {
      return this.$route.name === 'cart'
    },
    isDeliveryDateFallback () {
      return this.$store.state.deliveryDates.isDeliveryDateFallback
    },
    degradedMessages (): string[] {
      const messages: string[] = []
      const isGigyaFallback = this.$store.state.auth.isGigyaFallback
      if (isGigyaFallback && this.isDeliveryDateFallback) {
        messages.push(this.$t('At the moment, only standard orders for the next working day can be placed. Some portal sections are unavailable. We are working on a fix.'))
      } else if (isGigyaFallback) {
        messages.push(this.$t('At the moment, only standard orders can be placed, and My Profile section is unavailable. We are working on a fix.'))
      } else if (this.isDeliveryDateFallback) {
        messages.push(this.$t('At the moment, only standard orders for the next working day can be placed. Some portal sections are unavailable. We are working on a fix.'))
      }
      return messages
    },
    isDocumentPage () {
      return this.$route.name === 'myDocuments'
    },
    rightMenu () {
      return [
        ...this.checkPermission('My Profile', {
          label: 'My profile',
          href: '/my-profile/details',
          name: 'profile',
          isActive: false

        }),
        ...this.checkPermission('My Documents', {
          label: 'My Documents',
          href: '/my-documents',
          name: 'myDocuments',
          isActive: false,
          icon: 'newspaper'
        }),
        ...this.checkPermission('Reporting', {
          label: 'Reporting',
          href: '/reporting',
          name: 'reporting',
          isActive: false,
          icon: 'newspaper'
        }),
        ...this.checkPermission('Contracts', {
          label: 'Contracts',
          href: '/contracts',
          name: 'contracts',
          isActive: false,
          icon: 'file-contract'
        })
      ]
    },
    customerType (): RolePermissionDetails | undefined {
      if (this.user && this.user?.permissionSchema?.length > 0) {
        return this.user?.permissionSchema[0]?.rpDetails?.find((item: RolePermissionDetails) => item.customerType)
      }
      return undefined
    },
    user () {
      return this.$store.state.auth.user
    },
    allPermission () {
      return this.user.allAvailablePermission
    },
    portalHardCodeLink (): IPortalHardCodeLink[] {
      return this.$store.state.links.portalHardCodeLink?.results ?? []
    },
    favoritesItems () {
      return this.wishlist?.items_v2?.shipto_item_count
    },
    languages () {
      if (this.user.country === 'CZE' || window.location.hostname.includes('.cz')) {
        return ['cs', 'en']
      }

      if (this.user.country === 'SVK' || window.location.hostname.includes('.sk')) {
        return ['sk', 'en']
      }
      return ['cs', 'en', this.activeLanguage].filter((x, index, self) => x != null && self.indexOf(x) === index)
    },
    computedDictionary () {
      const activeLanguage = this.activeLanguage as keyof typeof this.dictionary
      return { ...this.dictionary, [activeLanguage]: { ...this.dictionary[activeLanguage] } }
    },
    portalPageMenuLink () {
      return this.$store.state.links.portalPageMenuLink?.items ?? []
    },
    cartItems () {
      return this.items?.length
    },
    catalogPermission () {
      return this.checkPermission('Catalogue', {}).length !== 0
    },
    footerLinksBrands () {
      let brandLinks

      if (this.user.country === 'SVK') {
        brandLinks = this.portalHardCodeLink
          .filter((data) => data.type.toLowerCase() === 'mobile brands sk' || data.type.toLowerCase() === 'mobile brands')
          .map((data) => ({
            label: data.label,
            link: data.url,
            target: '_blank',
            order: data.order
          }))
          .sort((a, b) => a.order - b.order)
      } else {
        brandLinks = this.portalHardCodeLink
          .filter((data) => data.type.toLowerCase() === 'mobile brands cz' || data.type.toLowerCase() === 'mobile brands')
          .map((data) => ({
            label: data.label,
            link: data.url,
            target: '_blank',
            order: data.order
          }))
          .sort((a, b) => a.order - b.order)
      }
      return [
        {
          title: 'brands'
        },
        ...brandLinks
      ]
    },
    footerLinksCookies () {
      const aboutUsLinks = this.portalHardCodeLink
        .filter((data) => data.name === 'cookies')
        .map((data) => ({
          label: this.$t('Cookies'),
          href: data.url,
          target: '_blank'
        }))
      return aboutUsLinks
    },
    footerLinksTermsOfUse () {
      const aboutUsLinks = this.portalHardCodeLink
        .filter((data) => data.name === 'termsOfUse')
        .map((data) => ({
          label: this.$t('Terms of use'),
          href: data.url,
          target: '_blank'
        }))
      return aboutUsLinks
    },
    footerLinksPrivacyPolicy () {
      let dataName = 'privacyPolicy'
      if (this.user?.country === 'SVK' || window.location.hostname.includes('.sk')) {
        dataName = 'privacyPolicySK'
      }
      const aboutUsLinks = this.portalHardCodeLink
        .filter((data) => data.name === dataName)
        .map((data) => ({
          label: this.$t('Privacy policy'),
          href: data.url,
          target: '_blank'
        }))
      return aboutUsLinks
    },
    isOnOtherPortalPages () {
      return this.$route.name !== 'products' && this.$route.name !== 'homepage' && this.$route.name !== 'favorites'
    }
  },

  methods: {
    changeLanguage (langCode: LanguagesType) {
      this.activeLanguage = langCode
      setI18nLanguage(langCode)
    },
    checkPermission (permission: string, value: Record<string, any>, additionalPremmision = true) {
      if (this.customerType) localStorage.setItem('customertype', this.customerType?.customerType)

      return this.allPermission?.includes(permission) &&
      additionalPremmision &&
      getValueByLabel(this.getPermissions(), permission)
        ? [value]
        : []
    },
    getLinkByName (name: string) {
      const linkObj = this.portalHardCodeLink.find((link) => link.name === name)
      return linkObj ? { url: linkObj.url, label: linkObj.label } : { url: '', label: '' }
    },
    async removeProductFromCart (productId: string) {
      if (this.$store.state.basket?.id && productId) {
        await this.$store.dispatch('basket/REMOVE_ITEM', {
          cart_id: this.$store.state.basket.id,
          cart_item_id: productId
        }, { root: true })
      }
    },
    fetchPortalHardCodeLink () {
      const customertype = localStorage.getItem('customertype') || ''
      const uid = sessionStorage.getItem('uid')

      this.$store.dispatch(
        LinksActionType.FETCH_PORTAL_HARD_CODE_LINK,
        { customertype, uid }
      )
    },

    fetchPortalPageMenuLink () {
      const customertype = localStorage.getItem('customertype') || ''
      const uid = sessionStorage.getItem('uid')

      this.$store.dispatch(
        LinksActionType.FETCH_PAGE_MENU_LINK,
        { customertype, uid }
      )
    },
    async fetchCartQuery () {
      if (!this.id || this.id === '') await this.$store.dispatch('basket/FETCH_BASKET_ID', {}, { root: true })
      if (this.id) {
        await this.$store.dispatch('basket/FETCH_CART_QUERY', { cart_id: this.id }, { root: true })
      }
    }
  },
  mounted () {
    if (this.isOnOtherPortalPages) {
      this.$store.dispatch('catalog/FETCH_WISHLIST', { bypassloading: true }, { root: true })
      this.fetchCartQuery()
    }
  },
  watch: {
    activeLanguage (newLang, oldLang) {
      if (newLang !== oldLang) {
        this.fetchPortalPageMenuLink()
      }
    }
  }
})
